import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import type { DocumentResponse } from '@timesheet/schemas';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { documentsService } from '../../services/documentsService';
import { useNotification } from '../../contexts/NotificationContext';

interface DocumentEditDialogProps {
  doc: DocumentResponse | null;
  open: boolean;
  onClose: () => void;
  onSaved: (doc: DocumentResponse) => void;
}

/** Title and description only: file, category, person and period are immutable. */
export const DocumentEditDialog: React.FC<DocumentEditDialogProps> = ({ doc, open, onClose, onSaved }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && doc) {
      setTitle(doc.title);
      setDescription(doc.description ?? '');
    }
  }, [open, doc]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!doc || !title.trim()) return;
    try {
      setSaving(true);
      const saved = await documentsService.update(doc.id, {
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
      });
      showNotification(t('documents.edit.success', 'Documento aggiornato'), 'success');
      onSaved(saved);
    } catch (error) {
      console.error('Error updating document:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('documents.edit.title', 'Modifica documento')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="document-title">{t('documents.edit.titleField', 'Titolo')}</Label>
            <Input id="document-title" value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="document-description">{t('documents.edit.description', 'Descrizione')}</Label>
            <Textarea id="document-description" value={description} maxLength={2000} rows={3} onChange={(event) => setDescription(event.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              {t('common.cancel', 'Annulla')}
            </Button>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600" disabled={saving || !title.trim()}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('documents.edit.save', 'Salva')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
